<template>
  <div class="phaseButtonContainer">
    <div id="skillBarControls" class="phaseControls btn-pair" :class="iAmOverdue ? 'iAmOverdue' : ''">
      <button
        v-for="(button, index) in buttons"
        :id="'sidebarButton-' + button.name"
        :key="index"
        :ref="(el) => setButtonElement(el, button.name)"
        class="halfButton"
        :class="[button.name]"
        :disabled="request === button.name || showError || pause === button.name"
        @click="handleButton($event, button)"
        @keyup.space="(e) => e.preventDefault()"
      >
        <transition name="basic" mode="out-in" class="buttonText">
          <span v-if="request !== button.name && pause !== button.name">{{ button.text }}</span>
        </transition>
        <transition name="basic" mode="out-in">
          <span v-if="request === button.name || pause === button.name" class="waiting">
            <span class="visuallyHidden">Please wait</span>
            <app-loading-spinner />
          </span>
        </transition>
      </button>
      <transition name="basic" mode="out-in">
        <div v-if="showError" class="error">
          {{ error }}
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue';
import _ from 'underscore';
import AppLoadingSpinner from '@/components/AppLoadingSpinner';
import { storeToRefs } from 'pinia';
import { useMeetingStore } from '@/store/pinia/meeting';
import { useMessagesStore } from '@/store/pinia/messages';
import { usePhaseStore } from '@/store/pinia/phase';
import { useSidebarStore } from '@/store/pinia/sidebar';
import { useUsersStore } from '@/store/pinia/users';
import { useTimeStore } from '@/store/pinia/time';
import { usePageStore } from '@/store/pinia/page';
import { useUiAnimations } from '@/composables/useUiAnimations';
import { startPhaseMap } from '@/resources/start-phase-map';
import { asUnit } from '@/resources/time-helpers';
import { nM } from '@/legacy';
import { videoSource } from '@/shared/constants';
import utils from '../resources/utils';

const props = defineProps({
  buttonSet: {
    default: 'auto',
  },
});

const { $ee, $meetingmanager, $API, $videomanager } = getCurrentInstance().appContext.config.globalProperties;

const meetingStore = useMeetingStore();
const messagesStore = useMessagesStore();
const phaseStore = usePhaseStore();
const sidebarStore = useSidebarStore();
const usersStore = useUsersStore();
const timeStore = useTimeStore();
const pageStore = usePageStore();

const { nopeWaggle } = useUiAnimations();

const controls = {
  request: { text: 'Setup', method: requestPhase },
  cancel: { text: 'Cancel', method: sidebarStore.requestPhaseSource },
  start: { text: 'Start', method: requestStart },
  back: { text: 'Back', method: requestBack },
  next: { text: 'Next', method: next },
  checkNext: { text: 'Next', method: requestCheckNext },
  done: { text: `I'm done`, method: done },
  finish: { text: 'Finish', method: requestEnd },
  moreTime: { text: 'More time', method: requestMoreTime, repeat: true },
  proposeDecision: { text: 'Next', method: requestPropose },
  setTopic: { text: 'Next', method: requestTopic },
  endVote: { text: 'Reveal', method: requestReveal },
  endResponding: { text: 'Review', method: requestReviewFeedback },
  iAmBack: { text: `I'm back`, method: back },
  finishEarly: { text: 'Finish Early', method: finishEarly },
  moveOn: { text: 'Finish', method: requestEnd },
  weAreDone: { text: `We're done`, method: weAreDone },
  leave: { text: 'Leave', method: getOut },
  end: { text: 'End', method: getOut },
};

const controlSets = {
  info: ['cancel', 'request'],
  setTime: ['back', 'start'],
  chooseUser: ['back', 'start'],
  running: ['moreTime', 'finish'],
  staged: ['moreTime', 'checkNext'],
  onStage: ['moreTime', 'done'],
  propose: ['moreTime', 'proposeDecision'],
  topic: ['moreTime', 'setTopic'],
  voting: ['moreTime', 'endVote'],
  givingFeedback: ['moreTime', 'endResponding'],
  breakAwayUser: ['iAmBack'],
  breakAwayOwner: ['moreTime', 'iAmBack'],
  breakBackUser: ['moveOn'],
  breakBackOwner: ['moreTime', 'moveOn'],
  groupsAwayUser: ['weAreDone'],
  groupsAwayOwner: ['moreTime', 'weAreDone'],
  groupsBackOwner: ['moreTime', 'moveOn'],
  groupsBackUser: ['moveOn'],
  endMeetingOwner: ['moreTime', 'end'],
  endMeetingUser: ['moreTime', 'leave'],
};
const buttonElements = ref({});

function setButtonElement(el, buttonName) {
  if (el) {
    buttonElements.value[buttonName] = el;
  }
}

const delayer = ref(false);
const delayerTimeout = ref(false);
const request = ref(false);
const error = ref(false);
const showError = ref(false);

$ee.on('nM:fullscreenMoreTime', requestMoreTime);

onBeforeUnmount(() => {
  $ee.off('nM:fullscreenMoreTime', requestMoreTime);
});

const {
  myId,
  speaking,
  active,
  iAmSpeaking,
  iAmActive,
  iAmModerator,
  usersOnline,
  usersHere,
  iAmAway,
  iAmOverdue,
  everyoneIsBack,
  iAmStillAway,
  iAmStillAwayInGroup,
  iAmAwayInGroup,
  randomTicker,
  groups,
} = storeToRefs(usersStore);

const { current: currentSidebar } = storeToRefs(sidebarStore);
const { currentPhase, currentPhaseData, phaseStage, settingUp, setupPhase, someoneIsMirroring, setupGroups, proposal, topic, mirroring } =
  storeToRefs(phaseStore);
const { phaseElapsed, settingTime, phaseSetTime, times } = storeToRefs(timeStore);
const { addedMoreTimeToBreak, addedMoreTimeToGroups, meetingId } = storeToRefs(meetingStore);
const { currentPageName } = storeToRefs(pageStore);

const screenshare = computed(() => currentSidebar.value.screensharePlease);
const skillBar = computed(() => currentSidebar.value.phase);
const barType = computed(() => currentSidebar.value.barType);
const iAmPhaseOwner = computed(() => iAmModerator.value || iAmSpeaking.value || iAmActive.value);

const controlSet = computed(() => {
  if (props.buttonSet !== 'auto' && controlSets[props.buttonSet]) {
    return props.buttonSet;
  }

  if (iAmStillAway.value) {
    return 'breakAwayUser';
  } else if (iAmStillAwayInGroup.value) {
    return 'groupsAwayUser';
  }
  if (barType.value === 'info') {
    return 'info';
  } else if (barType.value === 'setTime') {
    return 'setTime';
  } else if (!settingUp.value && currentPhase.value === 'decision') {
    const stage = phaseStage.value || 'proposal';
    return {
      proposal: 'propose',
      voting: 'voting',
      voted: 'running',
    }[stage];
  } else if (!settingUp.value && currentPhase.value === 'feedback') {
    const stage = phaseStage.value || 'topic';
    return {
      topic: 'topic',
      responding: 'givingFeedback',
      reviewing: 'running',
    }[stage];
  } else if (!settingUp.value && currentPhase.value === 'dialogue') {
    return phaseStage.value === 'topic' ? 'topic' : 'running';
  } else if (currentPhase.value === 'endMeeting' || currentPhase.value === 'ended') {
    return iAmModerator.value ? 'endMeetingOwner' : 'endMeetingUser';
  } else if (['checkIn', 'checkOut'].includes(skillBar.value)) {
    return iAmSpeaking.value ? 'onStage' : 'staged';
  } else if (currentPhase.value === 'breakTime') {
    return iAmAway.value ? ownerVariant('breakAway') : ownerVariant('breakBack');
  } else if (currentPhase.value === 'groups') {
    return iAmAwayInGroup.value ? ownerVariant('groupsAway') : ownerVariant('groupsBack');
  } else if (controlSets[barType.value]) {
    return barType.value;
  } else {
    return ['checkIn', 'checkOut'].includes(skillBar.value) ? 'staged' : 'running';
  }
});

const buttons = computed(() => {
  return controlSets[controlSet.value].map((c) => {
    let item = controls[c];
    item.name = c;
    return item;
  });
});

const pause = computed(() => {
  // Pause the request button if we're waiting for a random speaker to be chosen
  return randomTicker.value ? 'start' : false;
});

const seconds = computed(() => (phaseElapsed.value ? Math.floor(asUnit(phaseElapsed.value, 'seconds')) : 0));

function ownerVariant(str) {
  return iAmPhaseOwner.value ? `${str}Owner` : `${str}User`;
}

watch(
  controlSet,
  () => {
    delayer.value = true;
    delayerTimeout.value = setTimeout(() => {
      delayer.value = false;
    }, 1000);
  },
  { immediate: true, deep: true },
);

watch(currentPhase, (nv) => {
  if (nv !== 'breakTime') {
    meetingStore.setAddedMoreTimeToBreak(false);
  }
  if (nv !== 'groups') {
    meetingStore.setAddedMoreTimeToGroups(false);
  }
});

function handleButton(e, button) {
  if (button.repeat) {
    handleButtonRepeat(e, button);
  } else {
    handleButtonSingle(e, button);
  }
}

function handleButtonRaw(e, { method }) {
  if (seconds.value > 2 || !(speaking.value && speaking.value.id) || !['checkIn', 'checkOut'].includes(skillBar.value)) {
    if (method && _.isFunction(method)) {
      method();
    }
    e.target.blur();
  }
}

const handleButtonRepeat = _.throttle(handleButtonRaw, 500);
const handleButtonSingle = _.debounce(handleButtonRaw, 1000, true);

const addAlert = messagesStore.addAlert;
const interrupt = messagesStore.interrupt;

function jiggle(buttonName) {
  nopeWaggle(buttonElements.value[buttonName], { duration: 0.5 });
}
function cleanName(user) {
  return utils.desanitizeString(user.first_name);
}

function requestPhase() {
  let check = new Promise((resolve, reject) => {
    if (someoneIsMirroring.value) {
      addAlert('cannotStartWhileMirroring');
      reject('cannot');
    } else if (usersOnline.value.length < 2) {
      addAlert('pleaseWaitForUsersPhase');
      jiggle('request');
      reject('cannot');
    } else if (currentPhase.value === 'freeform') {
      resolve();
    } else {
      interrupt('sureYouWantToReplacePhase', {
        phase: currentSidebar.value.phase,
        speakingName: cleanName(speaking.value),
        activeName: cleanName(active.value),
      })
        .then(resolve)
        .catch(reject);
    }
  });

  check
    .then(() => {
      const groups = skillBar.value === 'groups' ? _.chunk(_.shuffle(_.pluck(usersHere.value, 'id')), Math.ceil(usersHere.value.length / 2)) : false;
      // const groups = skillBar.value === 'groups' ? [usersHere.value.map((user) => user.id)] : false;
      const phase = nM.phaseMapReverse[skillBar.value] || skillBar.value;
      const view = `${phase}Setup:time`;

      if (screenshare.value) {
        $meetingmanager.changeView(view, {
          source: videoSource.NONE,
        });
      } else if (groups) {
        $meetingmanager.changeView(view, { groups });
      } else {
        $meetingmanager.changeView(view);
      }
    })
    .catch((reason) => {
      if (reason === 'cannot') {
        jiggle('request');
      }
    });
}

function groupsSetupCheck(resolve) {
  let soloUsers = [];
  groups.value.forEach((group) => {
    if (group.length < 2) {
      const userName = cleanName(usersStore.getUserById(group[0]));
      soloUsers.push(userName);
    }
  });
  if (soloUsers.length < 1) {
    resolve();
  } else {
    addAlert('cannotStartWithUsersOnTheirOwn', { soloUsers });
    jiggle('start');
  }
}

function requestStart() {
  let check = new Promise((resolve) => {
    if (skillBar.value === 'groups') {
      groupsSetupCheck(resolve);
    } else if (iAmActive.value) {
      resolve();
    } else {
      interrupt('sureYouWantToStartPhase', {
        speakingName: cleanName(speaking.value),
        activeName: cleanName(active.value),
      })
        .then(resolve)
        .catch(() => {});
    }
  });

  check.then(() => {
    const params = startPhaseMap[setupPhase.value].params({
      phase: setupPhase.value,
      time: settingTime.value,
      maxTime: times.value[setupPhase.value]?.max || 0,
      speaking: speaking.value.id,
      groups: settingUp.value ? setupGroups.value : [],
    });
    const phase = nM.phaseMapReverse[setupPhase.value] || setupPhase.value;
    $meetingmanager.moveToPhase(phase, params);
    phaseStore.requestStartPhase(phase);
  });
}

function requestBack() {
  let check = new Promise((resolve) => {
    if (iAmActive.value) {
      resolve();
    } else {
      interrupt('sureYouWantToCancelSetup', {
        speakingName: cleanName(speaking.value),
        activeName: cleanName(active.value),
      })
        .then(resolve)
        .catch(() => {});
    }
  });

  check.then(() => {
    $meetingmanager.changeView(currentPhaseData.value.module_id);
    if (iAmActive.value) {
      sidebarStore.requestPhaseSource({ iCancelled: true });
    }
  });
}

function requestEnd() {
  let check = new Promise((resolve) => {
    if (delayer.value) {
      return false;
    } else if (currentPhase.value === 'freeform') {
      return false;
    } else if (currentPhase.value === 'groups' && !everyoneIsBack.value) {
      interrupt('notEveryoneIsBack', { phase: skillBar.value }).then(resolve);
    } else if (!iAmSpeaking.value && currentPhase.value !== 'groups' && speaking.value) {
      interrupt('sureYouWantToEndPhase', {
        phase: skillBar.value,
        speakingName: cleanName(speaking.value),
      })
        .then(resolve)
        .catch(() => {});
    } else {
      resolve();
    }
  });
  check.then(() => {
    $meetingmanager.endPhase();
  });
}

function requestCheckNext() {
  let check = new Promise((resolve) => {
    if (!speaking.value || !speaking.value.first_name) {
      resolve();
    } else {
      interrupt('timeLeftToCheckIn', { speakingName: cleanName(speaking.value), speakingId: speaking.value.id })
        .then(resolve)
        .catch(() => {});
    }
  });
  check.then(() => {
    next({
      user: myId.value,
      about: speaking.value.id,
    });
  });
}

function requestPropose() {
  let check = new Promise((resolve) => {
    if (speaking.value.id === myId.value) {
      resolve();
    } else {
      interrupt('sureYouWantToEndProposal', { speakingName: cleanName(speaking.value) })
        .then(resolve)
        .catch(() => {});
    }
  });

  check.then(() => {
    $meetingmanager.updateProposal({ proposal: proposal.value, finished: true });
  });
}

function requestTopic() {
  let check = new Promise((resolve) => {
    if (speaking.value.id === myId.value) {
      resolve();
    } else {
      interrupt('sureYouWantToEndTopic', { speakingName: cleanName(speaking.value), phase: currentPhase.value })
        .then(resolve)
        .catch(() => {});
    }
  });
  check.then(() => {
    $meetingmanager.updateTopic({ topic: topic.value, finished: true });
  });
}

function requestReveal() {
  interrupt('sureYouWantToReveal')
    .then(() => {
      $meetingmanager.endVoting();
    })
    .catch(() => {});
}

function requestReviewFeedback() {
  let check = new Promise((resolve) => {
    if (speaking.value.id === myId.value) {
      resolve();
    } else {
      interrupt('sureYouWantToReviewFeedback', { speakingName: cleanName(speaking.value) })
        .then(resolve)
        .catch(() => {});
    }
  });
  check.then(() => {
    let payload = {};
    if (someoneIsMirroring.value) {
      payload = { mirroringId: mirroring.value.mirroringId };
    }
    $meetingmanager.endResponding(payload);
  });
}

function requestMoreTime() {
  let check = new Promise((resolve) => {
    if (currentPhase.value === 'breakTime' && !addedMoreTimeToBreak.value) {
      interrupt('sureYouWantToAddTimeToBreak').then(() => {
        meetingStore.setAddedMoreTimeToBreak(true);
        resolve();
      });
    } else if (currentPhase.value === 'groups' && !addedMoreTimeToGroups.value) {
      interrupt('sureYouWantToAddTimeToGroups').then(() => {
        meetingStore.setAddedMoreTimeToGroups(true);
        resolve();
      });
    } else {
      resolve();
    }
  });
  check.then(() => {
    const amount =
      currentPhase.value === 'endMeeting' || (phaseSetTime.value <= 30 && !['breakTime', 'checkIn', 'checkOut'].includes(currentPhase.value)) ? 30 : 60;
    $meetingmanager.addTime(amount);

    timeStore.setAddingLocalTime(true);
    $ee.emit('nM:addTimeLocal');
  });
}

function getOut() {
  const ev = iAmModerator.value ? 'nM:meeting:finish' : 'nM:meeting:leave';
  $ee.emit('nM:meeting:fade');
  sidebarStore.meetingOver();
  $ee.emit(ev);
}

function next(args) {
  const payload = args.user === args.about ? false : args.about;
  $meetingmanager.finish(payload);
}

function done() {
  $meetingmanager.finish();
}

function finishEarly() {
  $meetingmanager.endingPhase();
}

function weAreDone() {
  $meetingmanager.haveReturned();
}

function back() {
  request.value = 'iAmBack';
  $API.getMeetingSession(meetingId.value).then(
    (session) => {
      $videomanager.init(session, {
        externalAttendeeId: myId.value,
        externalMeetingId: meetingId.value,
        pageName: currentPageName.value,
      });
      $meetingmanager.haveReturned();
      setTimeout(() => {
        request.value = false;
      }, 1000);
    },
    () => {
      request.value = false;
      handleError();
    },
  );
}

function handleError(msg) {
  msg = msg || 'please try again';
  error.value = msg;
  let thisError = msg;
  showError.value = true;
  _.delay(() => {
    if (error.value === thisError) {
      showError.value = false;
      _.delay(() => {
        error.value = false;
      }, 300);
    }
  }, 3000);
}
</script>
<style lang="scss" scoped>
.phaseButtonContainer {
  flex: 0 0 auto;
  //TODO: padding-top was 68px in dialogue for some reason, check this
  padding: rem(20px) 0 rem($log-height - 50px) 0;
  transition: all 0.5s cubic-bezier(0.6, 0, 0, 1);

  @media only screen and (min-height: $sidebar-timer-center-break) {
    flex: 0 0 auto;
  }
}

.phaseControls {
  --c__button: var(--c__text-energy-invert);
  border-width: 3px;
  height: rem(50px);
  position: relative;
  @include triggered {
    animation: none !important;
  }
}

@each $name in $skills {
  .microskillSidebar.#{$name} {
    .phaseControls {
      --c__button-bg: var(--c__skillbar-bg-#{$name}-solid);
    }
  }
}

.microskillSidebar.misc {
  .phaseControls {
    --c__button-bg: var(--c__bg);
    --c__button: var(--c__text);
  }
}

.error {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--c__bg);
  color: var(--c__warn);
  display: flex;
  justify-content: center;
  align-items: center;
}

@keyframes flash-skill-button {
  0%,
  3%,
  4%,
  7%,
  100% {
    color: var(--c__text-energy-invert);
    background: var(--c__button-bg);
  }

  1%,
  2%,
  5%,
  6% {
    color: var(--c__button-bg);
    background: var(--c__text-energy-invert);
  }
}

@keyframes flash-skill-button-constant {
  0%,
  20%,
  80%,
  100% {
    color: var(--c__text-energy-invert);
    background: var(--c__button-bg);
  }

  40%,
  60% {
    color: var(--c__button-bg);
    background: var(--c__text-energy-invert);
  }
}

.checkIn,
.checkOut {
  .phaseControls .done {
    animation: flash-skill-button 30s infinite linear 30s;
    @include triggered {
      animation: none;
    }
  }

  .phaseControls.iAmOverdue .done {
    animation: flash-skill-button-constant 1s infinite linear;
    @include triggered {
      animation: none;
    }
  }
}

.paused span {
  opacity: 0.5;
}

.waiting {
  position: absolute;
  top: 50%;
  left: 50%;
  margin: -0.5em 0 0 -0.5em;
}
</style>
